<template>
    <base-custom-settings :form-data="{}">
        <template #description>
            <text-heading sub>
                De opvolging is klaar. Wat wil je nu doen?
            </text-heading>
            <text-description :color="'white'">
                Je kunt het contact in Winck houden om een duidelijk overzicht van je campagnes en geschiedenis te bewaren.
                Of misschien wil je het contact na een tijdje verwijderen om je licentie beperkt te houden.
                De keuze is aan jou. Na afronding van de flow beslis jij over het lot van het contact.
                Ons advies? Bewaar een contact minstens 1.5 jaar. Zo heb je altijd een compleet beeld van je successen en houd je een dossier dat volledig inzicht biedt.
                Natuurlijk kun je dit altijd naar eigen inzicht aanpassen.
            </text-description>
        </template>

        <template #form>
            <flex-container :gap="Size.sm">
                <input-group
                    :model-value="selectedTimingTime"
                    :schema="object().required()"
                    @update:model-value="timingType = $event.value"
                >
                    <template #label>
                        <flex-container :gap="Size.sm">
                            <input-label>
                                <slot name="label">
                                    Contact verwijderen na
                                </slot>
                            </input-label>

                            <span v-if="false" v-tooltip.info="'Lorem ipsum'">
                                <svg-icon
                                    :icon="IconCircleInfo"
                                    :size="IconSize.sm"
                                    is-custom-icon
                                />
                            </span>
                        </flex-container>
                    </template>

                    <template #input>
                        <text-decorator size="xs">
                            <radio-group-input
                                label-key-name="label"
                                value-key-name="value"
                                :items="availableTimingTypes"
                            />
                        </text-decorator>
                    </template>
                </input-group>

                <template v-if="showTimingConfiguration">
                    <flex-container
                        no-wrap
                        align-items="center"
                    >
                        <flex-container :size="2">
                            Aantal
                        </flex-container>

                        <flex-container :size="2">
                            <input-group
                                v-model.number="timingQuantity"
                                :schema="number().required().moreThan(0).positive()"
                            >
                                <template #input>
                                    <text-input />
                                </template>
                            </input-group>
                        </flex-container>

                        <flex-container :size="8">
                            <input-group
                                :model-value="selectedTimingUnit"
                                :schema="object().required()"
                                @update:model-value="timingUnit = $event.value"
                            >
                                <template #input>
                                    <select-input :items="availableTimingUnits" />
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>

                    <flex-container>
                        <text-decorator
                            :size="FontSize.sm"
                            :color="'gray-400'"
                        >
                            Een contact wordt verwijderd als het nergens anders actief is.
                            Het moment van verwijderen hangt af van de laatste flow waarin het contact actief was.
                        </text-decorator>
                    </flex-container>
                </template>
            </flex-container>
        </template>
    </base-custom-settings>
</template>

<script setup lang="ts">
    import {
        FlexContainer,
        FontSize,
        IconSize,
        InputGroup,
        InputLabel,
        RadioGroupInput,
        Size,
        SvgIcon,
        TextDescription,
        TextHeading,
        TextInput,
    } from '@keyvue/one-ui';

    import { computed, type Ref, ref, watch } from 'vue';
    import { number, object } from 'yup';
    import type { Flow } from '@keyvue/one-api-client';
    import BaseCustomSettings from '@/flow/flow/components/graph/custom-types/CustomSettings.vue';
    import IconCircleInfo from '@keyvue/one-awesome-icons/icons/regular/circle-info.svg?component';

    interface Props {
        flow: Flow
    }

    type TimingType = 'never' | 'period';
    type TimingUnit = 'days' | 'weeks' | 'months';

    const emit = defineEmits<{
        (e: 'update:autoDeleteEnabled', value: boolean): void;
        (e: 'update:autoDeleteAfterQuantity', value: number): void;
        (e: 'update:autoDeleteAfterUnit', value: TimingUnit): void;
    }>();

    const props = defineProps<Props>();
    const timingType = ref(props.flow.autoDeleteEnabled ? 'period' : 'never');
    const timingQuantity = ref(props.flow.autoDeleteAfterQuantity ?? 18);
    const timingUnit = ref(props.flow.autoDeleteAfterUnit ?? 'months') as Ref<TimingUnit>;

    // v-model.number doesn't work with text-input, so we need to convert the value to a number
    const timingQuantityNumberValue = computed(() => Number.parseFloat(timingQuantity.value.toString()));

    const availableTimingTypes = computed<{ value: TimingType, label: string }[]>(() => ([
        {
            value: 'never',
            label: 'Nooit',
        },
        {
            value: 'period',
            label: 'Bepaalde periode',
        },
    ]));

    const availableTimingUnits = computed<{ value: TimingUnit, label: string }[]>(() => [
        {
            value: 'days',
            label: timingQuantityNumberValue.value === 1 ? 'dag' : 'dagen',
        },
        {
            value: 'weeks',
            label: timingQuantityNumberValue.value === 1 ? 'week' : 'weken',
        },
        {
            value: 'months',
            label: timingQuantityNumberValue.value === 1 ? 'maand' : 'maanden',
        },
    ]);

    const selectedTimingTime = computed(() => {
        return availableTimingTypes.value.find((entry) => entry.value === timingType.value);
    });

    const selectedTimingUnit = computed(() => {
        return availableTimingUnits.value.find((entry) => entry.value === timingUnit.value);
    });

    const showTimingConfiguration = computed(() => {
        return timingType.value === 'period';
    });

    watch(() => timingType.value, () => {
        // If autoDelete is disabled, we need to emit *all* values
        if (!props.flow.autoDeleteEnabled) {
            emit('update:autoDeleteAfterQuantity', timingQuantityNumberValue.value);
            emit('update:autoDeleteAfterUnit', timingUnit.value);
        }

        emit('update:autoDeleteEnabled', timingType.value === 'period');
    });

    watch(() => timingQuantity.value, () => {
        emit('update:autoDeleteAfterQuantity', timingQuantityNumberValue.value);
    });

    watch(() => timingUnit.value, () => {
        emit('update:autoDeleteAfterUnit', timingUnit.value);
    });
</script>
